import { addHours, addDays, format, isWeekend as dateFnsIsWeekend, isSameDay } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

/**
 * Check if a date is a weekend (Saturday or Sunday) in a given timezone
 */
export function isWeekend(date: Date, timezone: string): boolean {
  const zonedDate = toZonedTime(date, timezone);
  return dateFnsIsWeekend(zonedDate);
}

export interface Holiday {
  date: string; // YYYY-MM-DD format
  name: string;
}

/**
 * Check if a date is a holiday
 */
export function isHoliday(date: Date, holidays: Holiday[]): boolean {
  const dateStr = format(date, 'yyyy-MM-dd');
  return holidays.some(h => h.date === dateStr);
}

/**
 * Check if a date is a business day (not weekend or holiday)
 */
export function isBusinessDay(date: Date, holidays: Holiday[], timezone: string): boolean {
  const zonedDate = toZonedTime(date, timezone);
  
  if (isWeekend(zonedDate, timezone)) {
    return false;
  }
  
  if (isHoliday(zonedDate, holidays)) {
    return false;
  }
  
  return true;
}

/**
 * Subtract business days from a date
 * Skips weekends and holidays
 */
export function subtractBusinessDays(
  fromDate: Date,
  businessDays: number,
  holidays: Holiday[],
  timezone: string
): Date {
  let result = new Date(fromDate);
  let daysSubtracted = 0;
  
  while (daysSubtracted < businessDays) {
    result = addDays(result, -1);
    
    if (isBusinessDay(result, holidays, timezone)) {
      daysSubtracted++;
    }
  }
  
  return result;
}

/**
 * Add business days to a date
 * Skips weekends and holidays
 */
export function addBusinessDays(
  fromDate: Date,
  businessDays: number,
  holidays: Holiday[],
  timezone: string
): Date {
  let result = new Date(fromDate);
  let daysAdded = 0;
  
  while (daysAdded < businessDays) {
    result = addDays(result, 1);
    
    if (isBusinessDay(result, holidays, timezone)) {
      daysAdded++;
    }
  }
  
  return result;
}

/**
 * Get the next business day from a given date
 * If the date is already a business day, returns the same date
 */
export function getNextBusinessDay(
  date: Date,
  holidays: Holiday[],
  timezone: string
): Date {
  let result = new Date(date);
  
  while (!isBusinessDay(result, holidays, timezone)) {
    result = addDays(result, 1);
  }
  
  return result;
}

/**
 * Get the previous business day from a given date
 * If the date is already a business day, returns the same date
 */
export function getPreviousBusinessDay(
  date: Date,
  holidays: Holiday[],
  timezone: string
): Date {
  let result = new Date(date);
  
  while (!isBusinessDay(result, holidays, timezone)) {
    result = addDays(result, -1);
  }
  
  return result;
}

/**
 * Count business days between two dates (inclusive)
 */
export function countBusinessDays(
  startDate: Date,
  endDate: Date,
  holidays: Holiday[],
  timezone: string
): number {
  let count = 0;
  let current = new Date(startDate);
  
  while (current <= endDate) {
    if (isBusinessDay(current, holidays, timezone)) {
      count++;
    }
    current = addDays(current, 1);
  }
  
  return count;
}

/**
 * Calculate reminder time with business day awareness
 * If business day mode is enabled, "1 day before" becomes "1 business day before"
 */
export function calculateBusinessDayReminderTime(
  appointmentTime: Date,
  daysBeforeTarget: number,
  businessDayMode: boolean,
  holidays: Holiday[],
  timezone: string
): Date {
  if (!businessDayMode) {
    // Standard mode: simple day subtraction
    return addDays(appointmentTime, -daysBeforeTarget);
  }
  
  // Business day mode: skip weekends and holidays
  return subtractBusinessDays(appointmentTime, daysBeforeTarget, holidays, timezone);
}
